package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AdminStoriesPage
import com.arenaproplay.admin.domain.admin.AdminStory
import com.arenaproplay.admin.domain.me.FeedPage
import com.arenaproplay.admin.domain.me.FeedPost
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyLMedium
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlin.js.Date
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

// ── Delete target ─────────────────────────────────────────────────────────────

private sealed interface ModerationDeleteTarget {
    data class Story(val story: AdminStory) : ModerationDeleteTarget
    data class Post(val post: FeedPost) : ModerationDeleteTarget
}

// ── Avatar helpers ─────────────────────────────────────────────────────────────

private fun modPostInitials(name: String): String {
    val words = name.trim().split(" ", ".", "_", "-").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        name.length >= 2 -> "${name[0].uppercaseChar()}${name[1].uppercaseChar()}"
        name.isNotEmpty() -> name.first().uppercaseChar().toString()
        else -> "?"
    }
}

// ── Page ───────────────────────────────────────────────────────────────────────

/**
 * Модерация — Social content: KPI chips, stories strip, feed posts.
 * Mirrors /social content migrated to Night Arena dark glass.
 *
 * Old route /social redirects here.
 */
@Page("/moderation")
@Composable
fun ModerationPage() {
    val container = LocalAppContainer.current
    val repo = container.meRepository ?: return
    val adminRepo = container.adminRepository

    val scope = rememberCoroutineScope()
    var page by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(1) ?: 1) }
    var data by remember { mutableStateOf<FeedPage?>(null) }
    var storyPage by remember { mutableStateOf(1) }
    var stories by remember { mutableStateOf<AdminStoriesPage?>(null) }
    var postsReload by remember { mutableStateOf(0) }
    var storiesReload by remember { mutableStateOf(0) }
    val loadState = rememberLoadState()

    var pendingDelete by remember { mutableStateOf<ModerationDeleteTarget?>(null) }
    var deleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }

    // Sync URL state
    LaunchedEffect(page) {
        setUrlParams("page" to page.takeIf { it > 1 }?.toString())
    }

    LaunchedEffect(page, postsReload, loadState.reloadToken) {
        data = null
        loadState.load {
            data = repo.getFeed(page, 20)
        }
    }

    LaunchedEffect(storyPage, storiesReload, loadState.reloadToken) {
        if (adminRepo != null) {
            stories = null
            loadState.load {
                stories = adminRepo.getActiveStories(storyPage, 30)
            }
        }
    }

    AdminLayout(
        title = "Модерация",
        subtitle = "Лента публикаций и модерация контента платформы",
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        // ── Page title ─────────────────────────────────────────────────────────
        SpanText(
            "Модерация",
            Modifier.styleModifier {
                property("font-family", "'Unbounded', sans-serif")
                property("font-size", "24px")
                property("font-weight", "700")
                property("letter-spacing", "-0.02em")
                property("color", "#F0F4FC")
            }
        )

        VSpace(20)

        // ── KPI chips ──────────────────────────────────────────────────────────
        Row(
            Modifier.fillMaxWidth()
                .styleModifier { property("gap", "12px") }
        ) {
            ModerationKpiChip(
                label = "Публикации",
                value = data?.totalItems?.toString() ?: "…",
                loading = data == null,
                accentColor = "#0061FF",
                modifier = Modifier.styleModifier { property("flex", "1") },
            )
            ModerationKpiChip(
                label = "Активные истории",
                value = stories?.totalItems?.toString() ?: if (adminRepo != null) "…" else "—",
                loading = adminRepo != null && stories == null,
                accentColor = "#E4CD40",
                modifier = Modifier.styleModifier { property("flex", "1") },
            )
            ModerationKpiChip(
                label = "Всего лайков",
                value = data?.items?.sumOf { it.likesCount }?.toString() ?: "…",
                loading = data == null,
                accentColor = "#FF6B5B",
                modifier = Modifier.styleModifier { property("flex", "1") },
            )
            ModerationKpiChip(
                label = "Всего комментариев",
                value = data?.items?.sumOf { it.commentsCount }?.toString() ?: "…",
                loading = data == null,
                accentColor = "#00E5A4",
                modifier = Modifier.styleModifier { property("flex", "1") },
            )
        }

        VSpace(24)

        // ── Active stories section ─────────────────────────────────────────────
        if (adminRepo != null) {
            GlassCard {
                // Section header
                Row(
                    Modifier.fillMaxWidth()
                        .styleModifier {
                            property("align-items", "center")
                            property("gap", "8px")
                            property("margin-bottom", "16px")
                        }
                ) {
                    Column(Modifier.weight(1f)) {
                        SpanText(
                            "Активные истории",
                            Modifier.styleModifier {
                                property("font-size", "14px")
                                property("font-weight", "600")
                                property("color", "#F0F4FC")
                            }
                        )
                        Box(Modifier.height(3.px))
                        SpanText(
                            "Каждая история видна пользователям до истечения срока — удаление немедленное",
                            Modifier.styleModifier {
                                property("font-size", "12px")
                                property("color", "rgba(240,244,252,.55)")
                            }
                        )
                    }
                }

                ModerationStoriesStrip(
                    stories = stories?.items,
                    loading = stories == null,
                    onDelete = { pendingDelete = ModerationDeleteTarget.Story(it); deleteError = null },
                )

                val s = stories
                if (s != null && s.totalPages > 1) {
                    VSpace(12)
                    ModerationPager(
                        page = s.page,
                        totalPages = s.totalPages,
                        totalLabel = "${s.totalItems} историй",
                        onPrev = { if (storyPage > 1) storyPage-- },
                        onNext = { if (storyPage < s.totalPages) storyPage++ },
                    )
                }
            }

            VSpace(20)
        }

        // ── Feed posts section ─────────────────────────────────────────────────
        SectionCard(
            title = "Лента публикаций",
            subtitle = "Новые публикации сверху — действия модерации доступны администраторам",
        ) {
            val items = data?.items.orEmpty()
            when {
                data == null -> {
                    repeat(4) { idx -> ModerationFeedPostSkeleton(isLast = idx == 3) }
                }
                items.isEmpty() -> {
                    EmptyState(
                        icon = "📰",
                        title = "Публикаций пока нет",
                        hint = "Новые посты от пользователей появятся здесь",
                    )
                }
                else -> {
                    items.forEachIndexed { idx, post ->
                        ModerationFeedPostCard(
                            post = post,
                            onDelete = if (adminRepo != null) {
                                { pendingDelete = ModerationDeleteTarget.Post(post); deleteError = null }
                            } else {
                                null
                            },
                            isLast = idx == items.lastIndex,
                        )
                    }
                }
            }

            if (data != null && ((data?.totalPages ?: 1) > 1 || page > 1)) {
                VSpace(16)
                ModerationPager(
                    page = data?.page ?: page,
                    totalPages = data?.totalPages ?: 1,
                    totalLabel = "${data?.totalItems ?: 0} публикаций",
                    onPrev = { if (page > 1) page-- },
                    onNext = { if (page < (data?.totalPages ?: 1)) page++ },
                )
            }
        }

        VSpace(28)
    }

    // ── Confirm delete overlay — dark glass surface ────────────────────────────
    val target = pendingDelete
    ConfirmActionOverlay(
        isOpen = target != null,
        onClose = { if (!deleting) { pendingDelete = null; deleteError = null } },
        title = when (target) {
            is ModerationDeleteTarget.Story -> "Удалить историю"
            is ModerationDeleteTarget.Post -> "Удалить публикацию"
            null -> "Удалить"
        },
        description = when (target) {
            is ModerationDeleteTarget.Story ->
                "История пользователя «${target.story.authorName}» будет удалена навсегда. " +
                    "Изображение удаляется из хранилища и история исчезает для всех пользователей."
            is ModerationDeleteTarget.Post ->
                "Публикация пользователя «${target.post.authorName}» будет удалена вместе " +
                    "со всеми изображениями, лайками и комментариями."
            null -> ""
        },
        error = deleteError,
        confirmLabel = if (deleting) "Удаление…" else "Удалить",
        confirmDanger = true,
        confirmEnabled = !deleting,
        onConfirm = {
            if (target != null && adminRepo != null) {
                deleting = true
                deleteError = null
                scope.launch {
                    try {
                        when (target) {
                            is ModerationDeleteTarget.Story -> {
                                adminRepo.deleteStory(target.story.id)
                                val wasLastOnPage = stories?.items?.size == 1
                                if (wasLastOnPage && storyPage > 1) storyPage-- else storiesReload++
                            }
                            is ModerationDeleteTarget.Post -> {
                                adminRepo.deletePost(target.post.id, target.post.authorType)
                                postsReload++
                            }
                        }
                        pendingDelete = null
                    } catch (e: Throwable) {
                        deleteError = e.message ?: "Не удалось удалить"
                    } finally {
                        deleting = false
                    }
                }
            }
        },
    )
}

// ── KPI chip — Night Arena glass ───────────────────────────────────────────────

@Composable
private fun ModerationKpiChip(
    label: String,
    value: String,
    loading: Boolean,
    accentColor: String,
    modifier: Modifier = Modifier,
) {
    GlassCard(modifier = modifier, padding = 16) {
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-size", "11px")
                property("font-weight", "600")
                property("text-transform", "uppercase")
                property("letter-spacing", "0.08em")
                property("color", "rgba(240,244,252,.45)")
            }
        )
        Box(Modifier.height(6.px))
        if (loading) {
            Box(
                Modifier.fillMaxWidth().height(24.px)
                    .sBorderRadius(6)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.05)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
            )
        } else {
            Row(
                Modifier.styleModifier { property("align-items", "baseline"); property("gap", "8px") }
            ) {
                // Accent bar
                Box(
                    Modifier.width(3.px).height(20.px).sBorderRadius(3)
                        .styleModifier { property("background", accentColor) }
                )
                SpanText(
                    value,
                    Modifier.styleModifier {
                        property("font-family", "'Unbounded', sans-serif")
                        property("font-size", "22px")
                        property("font-weight", "700")
                        property("color", "#F0F4FC")
                        property("letter-spacing", "-0.02em")
                    }
                )
            }
        }
    }
}

// ── Stories strip ──────────────────────────────────────────────────────────────

@Composable
private fun ModerationStoriesStrip(
    stories: List<AdminStory>?,
    loading: Boolean,
    onDelete: (AdminStory) -> Unit,
) {
    when {
        loading || stories == null -> {
            Row(
                Modifier
                    .fillMaxWidth()
                    .overflow(Overflow.Auto)
                    .padding(bottom = 6.px),
            ) {
                repeat(5) { idx ->
                    if (idx > 0) HSpace(12)
                    Box(
                        Modifier
                            .width(130.px).minWidth(130.px).height(190.px)
                            .sBorderRadius(14)
                            .styleModifier {
                                property("background", "rgba(255,255,255,.05)")
                                property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                                property("flex-shrink", "0")
                            }
                    )
                }
            }
        }
        stories.isEmpty() -> {
            EmptyState(
                icon = "📷",
                title = "Активных историй нет",
                hint = "Пользовательские истории появятся здесь",
            )
        }
        else -> {
            Row(
                Modifier
                    .fillMaxWidth()
                    .overflow(Overflow.Auto)
                    .padding(bottom = 6.px),
            ) {
                stories.forEachIndexed { idx, story ->
                    if (idx > 0) HSpace(12)
                    ModerationStoryTile(story, onDelete = { onDelete(story) })
                }
            }
        }
    }
}

// ── Story tile ────────────────────────────────────────────────────────────────

@Composable
private fun ModerationStoryTile(story: AdminStory, onDelete: () -> Unit) {
    Column {
        Box(
            Modifier
                .width(130.px).minWidth(130.px).height(190.px)
                .sBorderRadius(14)
                .styleModifier {
                    property("background", "rgba(5,8,15,1)")
                    property("background-image", "url('${story.imageUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("position", "relative")
                    property("overflow", "hidden")
                    property("flex-shrink", "0")
                },
        ) {
            // Gradient overlay
            Box(
                Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 30%, " +
                            "rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)",
                    )
                },
            )

            // Delete button top-right
            Box(
                Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "8px")
                        property("right", "8px")
                    }
                    .width(28.px).height(28.px)
                    .borderRadius(50.percent)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("background", "rgba(0,0,0,.45)")
                        property("backdrop-filter", "blur(4px)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
                    .onClick { it.stopPropagation(); onDelete() },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "✕",
                    Modifier.styleModifier {
                        property("color", "#FF6B5B")
                        property("font-size", "11px")
                        property("font-weight", "700")
                        property("line-height", "1")
                    }
                )
            }

            // Author name + stats bottom
            Column(
                Modifier.styleModifier {
                    property("position", "absolute")
                    property("left", "10px")
                    property("right", "10px")
                    property("bottom", "8px")
                },
            ) {
                SpanText(
                    story.authorName,
                    Modifier.bodySMedium().styleModifier {
                        property("color", "#F0F4FC")
                        property("font-weight", "600")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("display", "block")
                    },
                )
                Box(Modifier.height(2.px))
                SpanText(
                    "👁 ${story.viewsCount} · ♥ ${story.likesCount}",
                    Modifier.mono(10).styleModifier {
                        property("color", "rgba(240,244,252,.85)")
                    },
                )
            }
        }

        Box(Modifier.height(6.px))
        SpanText(
            modExpiresIn(story.expiresAt),
            Modifier.mono(10).color(AppColors.Faint),
        )
    }
}

private fun modExpiresIn(expiresAt: String): String {
    val ms = Date(expiresAt).getTime() - Date.now()
    if (ms.isNaN() || ms <= 0) return "истекает…"
    val minutes = (ms / 60_000).toInt()
    return when {
        minutes >= 60 -> "истекает через ${minutes / 60}ч"
        else -> "истекает через ${minutes}м"
    }
}

// ── Feed post card ─────────────────────────────────────────────────────────────

@Composable
private fun ModerationFeedPostCard(post: FeedPost, onDelete: (() -> Unit)?, isLast: Boolean) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 16.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
            }
    ) {
        // Author row
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            val gradient = AppColors.AvatarPalette[
                kotlin.math.abs(post.authorId.hashCode()) % AppColors.AvatarPalette.size
            ]
            Box(
                Modifier
                    .width(44.px).minWidth(44.px).height(44.px)
                    .borderRadius(50.percent)
                    .styleModifier {
                        if (post.authorAvatarUrl != null) {
                            property("background-image", "url('${post.authorAvatarUrl}')")
                            property("background-size", "cover")
                            property("background-position", "center")
                        } else {
                            property("background", gradient)
                        }
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                if (post.authorAvatarUrl == null) {
                    SpanText(
                        modPostInitials(post.authorName),
                        Modifier.bodySMedium().styleModifier {
                            property("color", "#05080F")
                            property("font-weight", "700")
                            property("font-size", "15px")
                            property("line-height", "1")
                        },
                    )
                }
            }

            HSpace(12)

            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        post.authorName,
                        Modifier.bodyMMedium().color(AppColors.Ink)
                            .styleModifier {
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                    )
                    HSpace(8)
                    Pill(
                        text = modAuthorTypeLabel(post.authorType),
                        tone = if (post.authorType.equals("CLUB", true)) PillTone.Accent else PillTone.Neutral,
                    )
                    if (!post.city.isNullOrBlank()) {
                        HSpace(8)
                        SpanText(
                            "· ${post.city}",
                            Modifier.bodySMedium().color(AppColors.Faint),
                        )
                    }
                }
                Box(Modifier.height(2.px))
                SpanText(
                    formatTimestampShort(post.createdAt),
                    Modifier.mono(10).color(AppColors.Faint),
                )
            }

            HSpace(12)

            Row(verticalAlignment = Alignment.CenterVertically) {
                ModerationEngagementChip("♥", post.likesCount)
                HSpace(8)
                ModerationEngagementChip("💬", post.commentsCount)
            }

            if (onDelete != null) {
                HSpace(10)
                ModerationDeletePostButton(onDelete)
            }
        }

        // Post text (4-line clamp)
        if (post.content.isNotBlank()) {
            Box(Modifier.height(10.px))
            SpanText(
                post.content,
                Modifier.bodyMRegular().color(AppColors.Ink)
                    .styleModifier {
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "4")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("word-break", "break-word")
                        property("line-height", "1.55")
                    }
            )
        }

        // Media thumbnails
        if (post.images.isNotEmpty()) {
            Box(Modifier.height(12.px))
            Row(Modifier.fillMaxWidth()) {
                post.images.take(4).forEachIndexed { i, img ->
                    if (i > 0) HSpace(8)
                    Box(
                        Modifier
                            .weight(1f).height(120.px).sBorderRadius(10)
                            .styleModifier {
                                property("background", "rgba(255,255,255,.06)")
                                property("background-image", "url('${img.imageUrl}')")
                                property("background-size", "cover")
                                property("background-position", "center")
                            },
                    )
                }
            }
        }
    }
}

// ── Feed post skeleton ────────────────────────────────────────────────────────

@Composable
private fun ModerationFeedPostSkeleton(isLast: Boolean) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 16.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid rgba(255,255,255,.06)")
            }
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier.width(44.px).minWidth(44.px).height(44.px).borderRadius(50.percent)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.05)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        property("flex-shrink", "0")
                    }
            )
            HSpace(12)
            Column(Modifier.weight(1f)) {
                Box(
                    Modifier.height(13.px).sBorderRadius(6)
                        .styleModifier {
                            property("width", "45%")
                            property("background", "rgba(255,255,255,.05)")
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
                Box(Modifier.height(6.px))
                Box(
                    Modifier.height(11.px).sBorderRadius(6)
                        .styleModifier {
                            property("width", "25%")
                            property("background", "rgba(255,255,255,.03)")
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
            }
        }
        Box(Modifier.height(10.px))
        Box(
            Modifier.fillMaxWidth().height(13.px).sBorderRadius(6)
                .styleModifier {
                    property("background", "rgba(255,255,255,.03)")
                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                }
        )
        Box(Modifier.height(6.px))
        Box(
            Modifier.height(13.px).sBorderRadius(6)
                .styleModifier {
                    property("width", "70%")
                    property("background", "rgba(255,255,255,.03)")
                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                }
        )
    }
}

// ── Engagement chip ───────────────────────────────────────────────────────────

@Composable
private fun ModerationEngagementChip(glyph: String, count: Int) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 10.px, topBottom = 5.px)
            .styleModifier { property("background", "rgba(255,255,255,.05)") },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            SpanText(glyph, Modifier.mono(12).color(AppColors.Muted))
            HSpace(5)
            SpanText(
                count.toString(),
                Modifier.mono(12).color(AppColors.Ink)
                    .styleModifier { property("font-weight", "600") }
            )
        }
    }
}

// ── Delete post button ────────────────────────────────────────────────────────

@Composable
private fun ModerationDeletePostButton(onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", "rgba(255,107,91,.12)")
                property("border", "1px solid rgba(255,107,91,.25)")
                property("transition", "background 140ms ease")
            }
            .onClick {
                it.stopPropagation()
                onClick()
            },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                "✕",
                Modifier.mono(11).styleModifier {
                    property("color", "#FF6B5B")
                    property("font-weight", "700")
                }
            )
            HSpace(5)
            SpanText(
                "Удалить",
                Modifier.bodySMedium().color(AppColors.Error)
                    .styleModifier { property("font-weight", "600") },
            )
        }
    }
}

// ── Pager ─────────────────────────────────────────────────────────────────────

@Composable
private fun ModerationPager(
    page: Int,
    totalPages: Int,
    totalLabel: String,
    onPrev: () -> Unit,
    onNext: () -> Unit,
) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        SpanText(
            "Стр. $page из $totalPages · $totalLabel",
            Modifier.mono(10).color(AppColors.Muted),
        )
        Box(Modifier.weight(1f))
        PagerButton("‹", onPrev, page > 1)
        HSpace(6)
        PagerButton("›", onNext, page < totalPages)
    }
}

// ── Author type label ─────────────────────────────────────────────────────────

private fun modAuthorTypeLabel(type: String): String = when (type.uppercase()) {
    "CLUB" -> "Клуб"
    "USER" -> "Игрок"
    "TEAM" -> "Команда"
    else -> type.lowercase().replaceFirstChar { it.uppercaseChar() }
}
